import React, { useMemo, useRef } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPaperclip, faTimes } from '@fortawesome/free-solid-svg-icons'
import { useDocumentPreview } from 'src/hooks/useDocumentPreview'
import { useFileTypeIcon } from 'src/components/ModalForms/FileType/useFileTypeIcon'
import {
  QuestionnaireAttachment,
  QuestionnaireQuestion,
  QuestionnaireSupplementalInput,
} from './QuestionnaireTypes'
import { QuestionnaireFreeTextAnswer } from './QuestionnaireFreeTextAnswer'
import {
  AttachDocumentButton,
  AttachmentChip,
  AttachmentChipList,
  AttachmentChipName,
  AttachmentChipRemove,
  SupplementalContainer,
  SupplementalLabel,
} from './Questionnaire.styles'

// Attachments show their real file type, matching the attachments table and
// the playbook input list, rather than a generic paperclip.
export const AttachmentMimeIcon = ({ mime }: { mime: string }): JSX.Element => {
  const FileTypeIcon = useFileTypeIcon({
    mime: mime || 'application/octet-stream',
  })
  return <FileTypeIcon width="16px" height="16px" style={{ flexShrink: 0 }} />
}

const TEXT_INFO = 'TextInfo'
const ATTACHMENT = 'Attachment'
const DEFAULT_TEXT_PLACEHOLDER = 'Explain more ...'
// Used when an attachment slot names no particular document. An author can set
// a label to ask for a specific one ("Upload your 2025 SOC 2 report").
const DEFAULT_ATTACH_LABEL = 'Attach document'

// activeSupplementals resolves which slots apply right now: a question-level
// config always applies, a per-option one only while that option is selected.
//
// Both levels are returned together because the backend forbids the same Kind
// at both, so they cannot collide -- a question may still have, say,
// question-level text and per-option attachments.
export const activeSupplementals = (
  question: QuestionnaireQuestion,
  selectedValues: string[],
): QuestionnaireSupplementalInput[] => {
  const fromQuestion = question.supplementalList ?? []
  const fromOptions = question.optionsList
    .filter((option) => selectedValues.includes(option.label))
    .flatMap((option) => option.supplementalList ?? [])
  return [...fromQuestion, ...fromOptions]
}

export interface QuestionnaireSupplementalInputsProps {
  question: QuestionnaireQuestion
  // The option labels currently selected, so per-option slots can be gated.
  selectedValues: string[]
  supplementalText: string
  // Uncommitted supplemental text, owned by the caller so it survives the input
  // unmounting -- same contract as the free-text answer's draft.
  supplementalDraft: string | undefined
  onSupplementalDraftChange: (value: string) => void
  attachments: QuestionnaireAttachment[]
  disabled?: boolean
  readOnly?: boolean
  onSupplementalTextChange: (value: string) => void
  onAttach: (file: File) => void
  onRemoveAttachment: (attachmentId: string) => void
}

// QuestionnaireSupplementalInputs renders whatever a question additionally asks
// for beyond the answer itself: explanatory text, a document, or both.
//
// Shared by both answering surfaces. What the responder provides is stored
// against the answer rather than the slot that prompted it, so this component
// reports plain values and never mentions which supplemental produced them.
export const QuestionnaireSupplementalInputs = ({
  question,
  selectedValues,
  supplementalText,
  supplementalDraft,
  onSupplementalDraftChange,
  attachments,
  disabled = false,
  readOnly = false,
  onSupplementalTextChange,
  onAttach,
  onRemoveAttachment,
}: QuestionnaireSupplementalInputsProps): JSX.Element | null => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { openDocumentPreview } = useDocumentPreview()

  const active = useMemo(
    () => activeSupplementals(question, selectedValues),
    [question, selectedValues],
  )

  const textSlot = active.find((s) => s.kind === TEXT_INFO)
  const attachmentSlot = active.find((s) => s.kind === ATTACHMENT)

  // A FileUpload question is answered with a document, so it takes the same
  // upload affordance even without a configured attachment supplemental.
  const acceptsFiles =
    attachmentSlot !== undefined || question.responseType === 'FileUpload'

  if (!textSlot && !acceptsFiles) {
    return null
  }

  const isControlDisabled = disabled || readOnly
  // A single-file slot hides the prompt once a file exists; a list keeps it so
  // more can be added.
  const allowsMore = attachments.length === 0 || attachmentSlot?.isList === true

  return (
    <SupplementalContainer>
      {textSlot && (
        <>
          <SupplementalLabel htmlFor={`supplemental-${question.questionId}`}>
            {textSlot.label || DEFAULT_TEXT_PLACEHOLDER}
          </SupplementalLabel>
          {/* Committed explicitly with the same check/cross affordance as a
              free-text answer, so a half-typed explanation is never saved. */}
          <QuestionnaireFreeTextAnswer
            id={`supplemental-${question.questionId}`}
            value={supplementalText}
            draft={supplementalDraft}
            onDraftChange={onSupplementalDraftChange}
            placeholder={DEFAULT_TEXT_PLACEHOLDER}
            disabled={isControlDisabled}
            readOnly={readOnly}
            onChange={onSupplementalTextChange}
          />
        </>
      )}

      {acceptsFiles && (
        <>
          {attachments.length > 0 && (
            <AttachmentChipList>
              {attachments.map((attachment) => (
                <AttachmentChip key={attachment.attachmentId}>
                  <AttachmentMimeIcon mime={attachment.mimeType} />
                  <AttachmentChipName
                    type="button"
                    title={attachment.fileName}
                    onClick={() =>
                      openDocumentPreview(
                        attachment.contentId,
                        attachment.fileName,
                        attachment.mimeType,
                      )
                    }
                  >
                    {attachment.fileName}
                  </AttachmentChipName>
                  {!readOnly && (
                    <AttachmentChipRemove
                      type="button"
                      aria-label={`Remove ${attachment.fileName}`}
                      disabled={isControlDisabled}
                      onClick={() =>
                        onRemoveAttachment(attachment.attachmentId)
                      }
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </AttachmentChipRemove>
                  )}
                </AttachmentChip>
              ))}
            </AttachmentChipList>
          )}

          {!readOnly && allowsMore && (
            <>
              <AttachDocumentButton
                type="button"
                disabled={isControlDisabled}
                onClick={() => fileInputRef.current?.click()}
              >
                <FontAwesomeIcon icon={faPaperclip} />
                {attachmentSlot?.label || DEFAULT_ATTACH_LABEL}
              </AttachDocumentButton>
              <input
                ref={fileInputRef}
                type="file"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) onAttach(file)
                  // Reset so re-picking the same file fires onChange again.
                  e.target.value = ''
                }}
              />
            </>
          )}
        </>
      )}
    </SupplementalContainer>
  )
}
